function renderBuildEntityDevModeSnippetsImportsTemplate({
  singular,
  actionModulePath,
  camelSingular
}: {
  singular: string
  actionModulePath: string
  camelSingular: string
}): string {
  return `import { get${singular}ById } from '${actionModulePath}'

export default async function ${singular}Detail({ id }: { id: string }) {
  const ${camelSingular} = await get${singular}ById(id)

  if (!${camelSingular}) {
    return null
  }

  return <pre>{JSON.stringify(${camelSingular}, null, 2)}</pre>
}
`
}

function renderBuildEntityDevModeSnippetsImportsDetailsTemplate({
  singular,
  actionModulePath,
  camelSingular
}: {
  singular: string
  actionModulePath: string
  camelSingular: string
}): string {
  return `import { get${singular}BySlug } from '${actionModulePath}'

export default async function ${singular}Detail({ slug }: { slug: string }) {
  const ${camelSingular} = await get${singular}BySlug(slug)

  if (!${camelSingular}) {
    return null
  }

  return <pre>{JSON.stringify(${camelSingular}, null, 2)}</pre>
}
`
}

function renderBuildEntityDevModeSnippetsImportsLabelTemplate({
  plural,
  actionModulePath,
  camelPlural,
  toLowerCase
}: {
  plural: string
  actionModulePath: string
  camelPlural: string
  toLowerCase: string
}): string {
  return `import { get${plural} } from '${actionModulePath}'

export default async function ${plural}List() {
  const { ${camelPlural}, total } = await get${plural}({ limit: 10, offset: 0 })

  return (
    <section>
      <h2>{total} ${toLowerCase}</h2>
      <pre>{JSON.stringify(${camelPlural}, null, 2)}</pre>
    </section>
  )
}
`
}

function renderBuildEntityDevModeSnippetsClientModuleTemplate({
  singular,
  actionModulePath,
  tsObject,
  toLowerCase
}: {
  singular: string
  actionModulePath: string
  tsObject: string
  toLowerCase: string
}): string {
  return `'use client'

import * as React from 'react'
import { create${singular}, type ${singular}CreateInput } from '${actionModulePath}'

const input: ${singular}CreateInput = ${tsObject}

export function Create${singular}Button() {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await create${singular}(input)
        })
      }}
    >
      Create ${toLowerCase}
    </button>
  )
}
`
}

function renderBuildEntityDevModeSnippetsClientModuleSampleTemplate({
  singular,
  actionModulePath,
  tsObject,
  toLowerCase
}: {
  singular: string
  actionModulePath: string
  tsObject: string
  toLowerCase: string
}): string {
  return `'use client'

import * as React from 'react'
import { update${singular}, type ${singular}UpdateInput } from '${actionModulePath}'

export function Update${singular}Button({ id }: { id: string }) {
  const [isPending, startTransition] = React.useTransition()
  const input: ${singular}UpdateInput = ${tsObject}

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await update${singular}(input)
        })
      }}
    >
      Update ${toLowerCase}
    </button>
  )
}
`
}

function renderBuildEntityDevModeSnippetsClientModuleDetailsTemplate({
  singular,
  actionModulePath,
  toLowerCase
}: {
  singular: string
  actionModulePath: string
  toLowerCase: string
}): string {
  return `'use client'

import * as React from 'react'
import { delete${singular} } from '${actionModulePath}'

export function Delete${singular}Button({ id }: { id: string }) {
  const [isPending, startTransition] = React.useTransition()

  return (
    <button
      type="button"
      disabled={isPending}
      onClick={() => {
        startTransition(async () => {
          await delete${singular}(id)
        })
      }}
    >
      Delete ${toLowerCase}
    </button>
  )
}
`
}

export function renderBuildEntityDevModeSnippetsRenderer(
  request:
    | {
        kind: 'renderBuildEntityDevModeSnippetsImportsTemplate'
        props: {
          singular: string
          actionModulePath: string
          camelSingular: string
        }
      }
    | {
        kind: 'renderBuildEntityDevModeSnippetsImportsDetailsTemplate'
        props: {
          singular: string
          actionModulePath: string
          camelSingular: string
        }
      }
    | {
        kind: 'renderBuildEntityDevModeSnippetsImportsLabelTemplate'
        props: {
          plural: string
          actionModulePath: string
          camelPlural: string
          toLowerCase: string
        }
      }
    | {
        kind: 'renderBuildEntityDevModeSnippetsClientModuleTemplate'
        props: {
          singular: string
          actionModulePath: string
          tsObject: string
          toLowerCase: string
        }
      }
    | {
        kind: 'renderBuildEntityDevModeSnippetsClientModuleSampleTemplate'
        props: {
          singular: string
          actionModulePath: string
          tsObject: string
          toLowerCase: string
        }
      }
    | {
        kind: 'renderBuildEntityDevModeSnippetsClientModuleDetailsTemplate'
        props: {
          singular: string
          actionModulePath: string
          toLowerCase: string
        }
      }
): string {
  switch (request.kind) {
    case 'renderBuildEntityDevModeSnippetsImportsTemplate':
      return renderBuildEntityDevModeSnippetsImportsTemplate(request.props)
    case 'renderBuildEntityDevModeSnippetsImportsDetailsTemplate':
      return renderBuildEntityDevModeSnippetsImportsDetailsTemplate(request.props)
    case 'renderBuildEntityDevModeSnippetsImportsLabelTemplate':
      return renderBuildEntityDevModeSnippetsImportsLabelTemplate(request.props)
    case 'renderBuildEntityDevModeSnippetsClientModuleTemplate':
      return renderBuildEntityDevModeSnippetsClientModuleTemplate(request.props)
    case 'renderBuildEntityDevModeSnippetsClientModuleSampleTemplate':
      return renderBuildEntityDevModeSnippetsClientModuleSampleTemplate(request.props)
    case 'renderBuildEntityDevModeSnippetsClientModuleDetailsTemplate':
      return renderBuildEntityDevModeSnippetsClientModuleDetailsTemplate(request.props)
  }

  throw new Error('Unsupported renderer request')
}
